import React, { useState } from 'react';
import clsx from 'clsx';
import { Minus, Plus } from 'lucide-react';

export interface StepperProps {
    value: number;
    onChange: (next: number) => void;
    min?: number;
    max?: number;
    /** 30 · 36 · 44 px buttons */
    size?: 'sm' | 'md' | 'lg';
    disabled?: boolean;
    className?: string;
}

const sizes = {
    sm: { btn: 'w-[30px] h-[30px] [&>svg]:w-[15px] [&>svg]:h-[15px]', num: 'w-[30px] text-[15px]', gap: 'gap-1 p-[3px]' },
    md: { btn: 'w-9 h-9 [&>svg]:w-4 [&>svg]:h-4', num: 'w-[36px] text-[17px]', gap: 'gap-1 p-[3px]' },
    lg: { btn: 'w-11 h-11 [&>svg]:w-[18px] [&>svg]:h-[18px]', num: 'w-[40px] text-[19px]', gap: 'gap-1.5 p-1' },
};

/** Minus quiet, plus orange. */
export const Stepper: React.FC<StepperProps> = ({ value, onChange, min = 1, max = 1000, size = 'md', disabled, className }) => {
    const s = sizes[size];
    // Typed quantity is held locally until blur/Enter, then clamped to [min, max]
    const [draft, setDraft] = useState<string | null>(null);
    const commit = () => {
        if (draft !== null) {
            const n = parseInt(draft, 10);
            if (Number.isFinite(n)) onChange(Math.min(max, Math.max(min, n)));
            setDraft(null);
        }
    };
    return (
        <div className={clsx('inline-flex items-center rounded-pill bg-surface-sunken', s.gap, disabled && 'opacity-50 pointer-events-none', className)}>
            <button
                type="button"
                aria-label="Decrease"
                disabled={value <= min}
                onClick={() => onChange(value - 1)}
                className={clsx('inline-flex items-center justify-center rounded-full bg-surface-card text-ink-primary shadow-xs transition-transform active:scale-[.92] disabled:opacity-40', s.btn)}
            >
                <Minus />
            </button>
            <input
                type="text"
                inputMode="numeric"
                aria-label="Quantity"
                value={draft ?? String(value)}
                onFocus={(e) => e.currentTarget.select()}
                onChange={(e) => setDraft(e.target.value.replace(/\D/g, ''))}
                onBlur={commit}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur(); }}
                className={clsx('bg-transparent text-center font-black tnum outline-none', s.num)}
            />
            <button
                type="button"
                aria-label="Increase"
                disabled={value >= max}
                onClick={() => onChange(value + 1)}
                className={clsx('inline-flex items-center justify-center rounded-full bg-accent-500 text-ink-onAccent transition-transform active:scale-[.92] disabled:opacity-40', s.btn)}
            >
                <Plus />
            </button>
        </div>
    );
};
export default Stepper;
